import { useEffect, useMemo, useRef, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, FARM_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { RowActions } from "@/components/RowActions";
import { AnimalDialog } from "@/components/AnimalDialog";
import { AnimalWarnings } from "@/components/AnimalWarnings";
import { AnimalStatusForm } from "@/components/AnimalStatusForm";
import { AnimalDetail } from "@/components/AnimalDetail";
import { BulkAnimalsDialog } from "@/components/BulkAnimalsDialog";
import { CalfCropDialog } from "@/components/CalfCropDialog";
import { LivestockBatchDialog } from "@/components/LivestockBatchDialog";
import { useLivestock } from "@/hooks/useLivestock";
import { problemMessage } from "@/api/client";
import type {
  Animal,
  AnimalGroup,
  AnimalWarning,
  ChangeAnimalStatusRequest,
  Guid,
  Herd,
} from "@/api/types";
import "./Livestock.css";

/**
 * ef-041-5 — the owner's Livestock screen, `/farm/livestock`: herd → group → animal
 * browsing with a derived active headcount, create/edit, a status-change action with a
 * reason, and an animal detail view showing its own audit trail
 * (`GET .../status-history`, this slice's one API addition). Per ADR 0009's "one page
 * template" — `PageHeader` (title + the one primary action, "New animal") → the Farm area
 * tab strip → content in cards.
 *
 * GROUPED BY HERD, NOT FILTERED. Rather than a filter control this reuses `Schedule.tsx`'s
 * own "group by week" shape: every herd renders as its own card (present even with zero
 * animals — an owner who just created one should see it), animals with no herd fall into
 * a trailing "Unassigned" card that only renders when non-empty. Headcounts are DERIVED —
 * `useMemo` over the roster `useLivestock.ts` already holds, never a second fetch or a
 * synced counter (State Management Guidelines: "Derived state — compute it, do not store
 * it").
 *
 * ONE PRIMARY ACTION PER ROW (ADR 0009 §4) — Edit is the row's own visible primary
 * button; "View" sits beside it as the row's identity/detail affordance (same class of
 * control as `PayRuns.tsx`'s own history-row link, not a mutating action); Change
 * status/Delete sit behind `RowActions`'s "More actions" disclosure, exactly
 * `Schedule.tsx`'s own precedent.
 */

function formatWeight(weight: number | null): string {
  return weight != null ? `${weight} lbs` : "—";
}

interface HerdSection {
  key: string;
  /** `null` only for the trailing "Unassigned" bucket — every real herd carries its own
   *  id, which `HerdHeading` needs to archive it. */
  herdId: Guid | null;
  heading: string;
  breed: string | null;
  animals: Animal[];
  activeCount: number;
  totalCount: number;
}

/** Groups the roster into herd sections, "Unassigned" trailing and only present when
 *  non-empty. Never re-derived elsewhere — this is the one place "which herd does this
 *  animal belong to, on screen" is decided. `herds` is ACTIVE ONLY (`useLivestock`'s own
 *  split) — an archived herd's animals fall into "Unassigned" the same way a herd-less
 *  animal's do, since their `herdId` is untouched (0043's header) but the herd it names
 *  is no longer a visible section of its own. */
function groupByHerd(herds: Herd[], animals: Animal[]): HerdSection[] {
  const byHerd = new Map<Guid, { herd: Herd; animals: Animal[] }>(
    herds.map((herd) => [herd.id, { herd, animals: [] }]),
  );
  const unassigned: Animal[] = [];

  for (const animal of animals) {
    const bucket = animal.herdId ? byHerd.get(animal.herdId) : undefined;
    if (bucket) bucket.animals.push(animal);
    else unassigned.push(animal);
  }

  const sortByRecency = (list: Animal[]) => [...list].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  const sections: HerdSection[] = [...byHerd.values()]
    .sort((a, b) => a.herd.name.localeCompare(b.herd.name))
    .map(({ herd, animals: list }) => ({
      key: herd.id,
      herdId: herd.id,
      heading: herd.name,
      breed: herd.breed,
      animals: sortByRecency(list),
      activeCount: list.filter((a) => a.status === "active").length,
      totalCount: list.length,
    }));

  if (unassigned.length > 0) {
    sections.push({
      key: "unassigned",
      herdId: null,
      heading: "Unassigned",
      breed: null,
      animals: sortByRecency(unassigned),
      activeCount: unassigned.filter((a) => a.status === "active").length,
      totalCount: unassigned.length,
    });
  }

  return sections;
}

export function Livestock() {
  const {
    herds,
    archivedHerds,
    groups,
    animals,
    entityId,
    status,
    error,
    hasMore,
    loadingMore,
    loadMoreError,
    loadMore,
    createAnimal,
    updateAnimal,
    deleteAnimal,
    archiveHerd,
    restoreHerd,
    changeAnimalStatus,
    getAnimalHistory,
    getAnimalWeights,
    addAnimalWeight,
    postPurchase,
    postSale,
    createAnimalsBulk,
    createCalfCrop,
  } = useLivestock();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Animal | null>(null);
  const [viewing, setViewing] = useState<Animal | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // ef-059-6 — non-blocking findings from the last SUCCESSFUL animal save (duplicate tag).
  const [warnings, setWarnings] = useState<AnimalWarning[]>([]);
  // ef-042-0b — the archived-herds disclosure, same shape as Places.tsx's own.
  const [archivedHerdsOpen, setArchivedHerdsOpen] = useState(false);
  const [restoringHerdId, setRestoringHerdId] = useState<Guid | null>(null);
  const [restoreHerdError, setRestoreHerdError] = useState<string | null>(null);
  // ef-041-6 — "Buy" (page-level, no animal preselected) and "Sell" (row-level, always
  // opened WITH one) are two modes of the same LivestockBatchDialog. `sellingAnimal` doubles
  // as both "which animal" and "is the sale dialog open" — never a second boolean that
  // could drift from it.
  const [buyOpen, setBuyOpen] = useState(false);
  const [sellingAnimal, setSellingAnimal] = useState<Animal | null>(null);
  // ef-059-8 — bulk entry + calf crop dialogs.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [calfCropOpen, setCalfCropOpen] = useState(false);

  const sections = useMemo(() => groupByHerd(herds ?? [], animals ?? []), [herds, animals]);

  function openCreate(): void {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(animal: Animal): void {
    setEditing(animal);
    setDialogOpen(true);
  }

  function closeDialog(): void {
    setDialogOpen(false);
    setEditing(null);
  }

  async function deleteOne(id: Guid): Promise<void> {
    setActionError(null);
    try {
      await deleteAnimal(id);
    } catch (err) {
      setActionError(problemMessage(err, "Could not delete this animal."));
    }
  }

  /** ef-042-0b — restore straight from the archived-herds disclosure's own row; no
   *  confirm dialog, same reasoning `Places.tsx`'s own restore uses: bringing a herd back
   *  destroys nothing. */
  async function handleRestoreHerd(id: Guid): Promise<void> {
    setRestoringHerdId(id);
    setRestoreHerdError(null);
    try {
      await restoreHerd(id);
    } catch (err) {
      setRestoreHerdError(problemMessage(err, "Could not restore this herd."));
    } finally {
      setRestoringHerdId(null);
    }
  }

  return (
    <main className="livestock-page">
      <PageHeader
        title="Livestock"
        subtitle="Herds, groups and every animal on the roster."
        action={
          <span className="livestock-page__header-actions">
            <button type="button" className="btn btn--ghost" onClick={() => setBuyOpen(true)}>
              Buy
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setBulkOpen(true)}>
              Bulk add
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setCalfCropOpen(true)}>
              Calf crop
            </button>
            <button type="button" className="btn btn--primary" onClick={openCreate}>
              New animal
            </button>
          </span>
        }
      />

      <AreaTabs area="/farm" tabs={FARM_TABS} label="Farm" />

      <AnimalWarnings warnings={warnings} onDismiss={() => setWarnings([])} />

      <section
        className="livestock-page__card"
        aria-labelledby="livestock-heading"
        data-state={status === "ready" && sections.length === 0 ? "empty" : status}
      >
        <h2 id="livestock-heading" className="livestock-page__card-title">
          Roster
        </h2>

        {status === "loading" && <StateMessage tone="loading" title="Loading the roster…" />}

        {status === "error" && (
          <StateMessage tone="error" title="Could not load the roster." detail={error ?? undefined} />
        )}

        {status === "ready" && sections.length === 0 && (
          <StateMessage
            tone="empty"
            title="No animals yet."
            detail="Add your first animal and it will show up here, grouped by herd."
          />
        )}

        {actionError && (
          <p className="livestock-page__error" role="alert">
            {actionError}
          </p>
        )}

        {status === "ready" && sections.length > 0 && (
          <div className="livestock-page__herds">
            {sections.map((section) => (
              <div key={section.key} className="livestock-page__herd">
                <HerdHeading section={section} onArchive={archiveHerd} />
                <ul className="livestock-page__list" aria-label={section.heading}>
                  {section.animals.map((animal) => (
                    <AnimalRow
                      key={animal.id}
                      animal={animal}
                      groups={groups ?? []}
                      onEdit={openEdit}
                      onView={setViewing}
                      onDelete={deleteOne}
                      onChangeStatus={changeAnimalStatus}
                      onSell={setSellingAnimal}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {status === "ready" && sections.length > 0 && (
          // ef-042-0c — paged past `ANIMALS_PAGE_LIMIT` (200): the count is a live region
          // so a screen reader hears the roster grow after "Load more animals", not just a
          // sighted user watching the list lengthen. `hasMore` (not the button's own
          // presence) is the single source of truth for whether there's more to fetch —
          // the button, not a second boolean, follows it.
          <div className="livestock-page__paging">
            <span className="livestock-page__paging-count" role="status" aria-live="polite">
              {animals?.length ?? 0} of {animals?.length ?? 0}
              {hasMore ? "+" : ""} shown
            </span>
            {hasMore && (
              <button
                type="button"
                className="btn btn--ghost livestock-page__btn"
                onClick={() => void loadMore()}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading…" : "Load more animals"}
              </button>
            )}
            {loadMoreError && (
              <p className="livestock-page__error" role="alert">
                {loadMoreError}
              </p>
            )}
          </div>
        )}

        {/* ef-042-0b — a <fieldset>/<legend> pair, same shape as Places.tsx's own archived
            disclosure, for the same reason: a real accessible NAME without a second
            visible heading repeating the toggle button's own label. */}
        {status === "ready" && (
          <fieldset className="livestock-page__archived-herds">
            <legend className="visually-hidden">Archived herds</legend>
            <button
              type="button"
              className="btn btn--ghost"
              aria-expanded={archivedHerdsOpen}
              onClick={() => setArchivedHerdsOpen((v) => !v)}
            >
              {archivedHerdsOpen ? "Hide" : "Show"} archived herds
              {archivedHerds.length > 0 ? ` (${archivedHerds.length})` : ""}
            </button>

            {archivedHerdsOpen && (
              <>
                {restoreHerdError && (
                  <p className="livestock-page__field-error" role="alert">
                    {restoreHerdError}
                  </p>
                )}
                {archivedHerds.length === 0 ? (
                  <p className="livestock-page__sub">Nothing archived yet.</p>
                ) : (
                  <ul className="livestock-page__list" aria-label="Archived herds">
                    {archivedHerds.map((herd) => (
                      <li key={herd.id} className="livestock-page__row">
                        <span className="livestock-page__meta">
                          <span className="livestock-page__tag">{herd.name}</span>
                          {herd.breed && <span className="livestock-page__sub">{herd.breed}</span>}
                        </span>
                        <span className="livestock-page__actions">
                          <button
                            type="button"
                            className="btn btn--ghost livestock-page__btn"
                            disabled={restoringHerdId === herd.id}
                            onClick={() => void handleRestoreHerd(herd.id)}
                          >
                            {restoringHerdId === herd.id ? "Restoring…" : "Restore"}
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </fieldset>
        )}
      </section>

      {dialogOpen && (
        <AnimalDialog
          entityId={entityId}
          herds={herds ?? []}
          groups={groups ?? []}
          animal={editing}
          onClose={closeDialog}
          onCreate={createAnimal}
          onUpdate={updateAnimal}
          onSaved={(saved) => setWarnings(saved.warnings ?? [])}
        />
      )}

      {viewing && (
        <AnimalDetail
          animal={viewing}
          getHistory={getAnimalHistory}
          getWeights={getAnimalWeights}
          addWeight={addAnimalWeight}
          onClose={() => setViewing(null)}
        />
      )}

      {bulkOpen && (
        <BulkAnimalsDialog
          entityId={entityId}
          herds={herds ?? []}
          onClose={() => setBulkOpen(false)}
          onSubmit={createAnimalsBulk}
        />
      )}

      {calfCropOpen && (
        <CalfCropDialog
          animals={animals ?? []}
          herds={herds ?? []}
          onClose={() => setCalfCropOpen(false)}
          onSubmit={createCalfCrop}
        />
      )}

      {buyOpen && (
        <LivestockBatchDialog
          kind="purchase"
          entityId={entityId}
          animals={animals ?? []}
          onClose={() => setBuyOpen(false)}
          onSubmitPurchase={postPurchase}
          onSubmitSale={postSale}
        />
      )}

      {sellingAnimal && (
        <LivestockBatchDialog
          kind="sale"
          entityId={entityId}
          animals={animals ?? []}
          initialAnimal={sellingAnimal}
          onClose={() => setSellingAnimal(null)}
          onSubmitPurchase={postPurchase}
          onSubmitSale={postSale}
        />
      )}
    </main>
  );
}

function AnimalRow({
  animal,
  groups,
  onEdit,
  onView,
  onDelete,
  onChangeStatus,
  onSell,
}: {
  animal: Animal;
  groups: AnimalGroup[];
  onEdit: (animal: Animal) => void;
  onView: (animal: Animal) => void;
  onDelete: (id: Guid) => Promise<void>;
  onChangeStatus: (id: Guid, body: ChangeAnimalStatusRequest) => Promise<void>;
  /** ef-041-6 — opens Livestock.tsx's own page-level sale dialog, pre-locked to this
   *  animal. Only ever offered for an `active` animal (see the menu below) — a non-active
   *  one already carries no way to reach this at all, same "hide it, don't just refuse it"
   *  precedent as every other status-gated control on this row. */
  onSell: (animal: Animal) => void;
}) {
  const [activePanel, setActivePanel] = useState<"menu" | "status" | "delete">("menu");
  const [deleting, setDeleting] = useState(false);
  // Bumped after a successful status change to force RowActions to remount closed — it
  // owns its own `open` state and has no external "close" prop (same reasoning
  // Schedule.tsx's own ShiftRow never needed this: a completed delete removes the row
  // instead, but a status change leaves it in place).
  const [panelKey, setPanelKey] = useState(0);
  // After a completed status change the remount below destroys the focused submit button, which
  // would drop focus to <body>; this flag sends it to the fresh "More actions" trigger instead.
  const rowRef = useRef<HTMLLIElement>(null);
  const refocusTrigger = useRef(false);
  useEffect(() => {
    if (!refocusTrigger.current) return;
    refocusTrigger.current = false;
    rowRef.current?.querySelector<HTMLButtonElement>(".row-actions__trigger")?.focus();
  }, [panelKey]);

  const groupName = animal.groupId ? (groups.find((g) => g.id === animal.groupId)?.name ?? null) : null;

  function resetPanel(): void {
    setActivePanel("menu");
  }

  async function confirmDelete(): Promise<void> {
    setDeleting(true);
    await onDelete(animal.id);
    setDeleting(false);
  }

  // Force RowActions to remount closed the same way a completed status change does — the
  // batch dialog that opens next is a page-level modal, not a row-local panel, so this
  // menu should not still be showing behind it.
  function sell(): void {
    setPanelKey((k) => k + 1);
    onSell(animal);
  }

  return (
    <li ref={rowRef} className="livestock-page__row" data-animal-id={animal.id} data-state={animal.status}>
      <span className="livestock-page__meta">
        <span className="livestock-page__tag">{animal.tag}</span>
        <span className="livestock-page__sub">
          {[
            animal.breed,
            groupName,
            animal.brand,
            animal.color,
            animal.ownerName ? `Owner: ${animal.ownerName}` : null,
            animal.twin ? "Twin" : null,
          ]
            .filter(Boolean)
            .join(" · ") || "—"}
        </span>
      </span>

      <span className="livestock-page__weight">{formatWeight(animal.weightCurrent)}</span>

      <span className="livestock-page__status">{animal.status}</span>

      <span className="livestock-page__actions">
        <button type="button" className="btn btn--ghost livestock-page__btn" onClick={() => onView(animal)}>
          View
        </button>
        <button type="button" className="btn btn--primary livestock-page__btn" onClick={() => onEdit(animal)}>
          Edit
        </button>

        <RowActions key={panelKey} onOpenChange={(open) => !open && resetPanel()}>
          {activePanel === "menu" && (
            <span className="livestock-page__menu">
              {animal.status === "active" && (
                <button type="button" className="btn btn--ghost livestock-page__btn" onClick={sell}>
                  Sell
                </button>
              )}
              <button
                type="button"
                className="btn btn--ghost livestock-page__btn"
                onClick={() => setActivePanel("status")}
              >
                Change status
              </button>
              <button
                type="button"
                className="btn btn--ghost livestock-page__btn"
                onClick={() => setActivePanel("delete")}
              >
                Delete
              </button>
            </span>
          )}

          {activePanel === "status" && (
            <AnimalStatusForm
              animalId={animal.id}
              currentStatus={animal.status}
              onSubmit={onChangeStatus}
              onBack={() => setActivePanel("menu")}
              // A completed change leaves the row in place: remount RowActions closed.
              onDone={() => {
                refocusTrigger.current = true;
                setPanelKey((k) => k + 1);
              }}
            />
          )}

          {activePanel === "delete" && (
            <span className="livestock-page__confirm">
              <span className="livestock-page__confirm-copy">Delete this animal? This cannot be undone.</span>
              <button
                type="button"
                className="btn btn--danger livestock-page__btn"
                disabled={deleting}
                onClick={() => void confirmDelete()}
              >
                {deleting ? "…" : "Delete"}
              </button>
              <button
                type="button"
                className="btn btn--ghost livestock-page__btn"
                disabled={deleting}
                onClick={() => setActivePanel("menu")}
              >
                Cancel
              </button>
            </span>
          )}
        </RowActions>
      </span>
    </li>
  );
}

/**
 * ef-042-0b — a herd section's own heading, carrying the one action a herd has: Archive,
 * behind `RowActions`'s "More actions" disclosure with an inline confirm panel — same
 * shape as `AnimalRow`'s own delete confirm. Never rendered with an action for the
 * trailing "Unassigned" bucket (`section.herdId` is `null` there).
 */
function HerdHeading({
  section,
  onArchive,
}: {
  section: HerdSection;
  onArchive: (id: Guid) => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  // Same "force the disclosure closed after a completed action" convention AnimalRow's
  // own panelKey uses — a herd whose archive just succeeded should not still show an open
  // confirm panel for a heading that is about to disappear from this list.
  const [panelKey, setPanelKey] = useState(0);

  async function confirmArchive(): Promise<void> {
    if (!section.herdId) return;
    setArchiving(true);
    setArchiveError(null);
    try {
      await onArchive(section.herdId);
      setPanelKey((k) => k + 1);
    } catch (err) {
      setArchiveError(problemMessage(err, "Could not archive this herd."));
    } finally {
      setArchiving(false);
    }
  }

  return (
    <h3 className="livestock-page__herd-heading">
      <span>{section.heading}</span>
      {section.breed && <span className="livestock-page__herd-breed">{section.breed}</span>}
      <span className="livestock-page__herd-count">
        {section.activeCount} active · {section.totalCount} total
      </span>

      {section.herdId && (
        <RowActions
          key={panelKey}
          label="Herd actions"
          onOpenChange={(open) => {
            if (!open) {
              setConfirming(false);
              setArchiveError(null);
            }
          }}
        >
          {!confirming ? (
            <span className="livestock-page__menu">
              <button
                type="button"
                className="btn btn--ghost livestock-page__btn"
                onClick={() => setConfirming(true)}
              >
                Archive herd
              </button>
            </span>
          ) : (
            <span className="livestock-page__confirm">
              <span className="livestock-page__confirm-copy">
                Archive {section.heading}? Its animals stay exactly where they are — only
                the herd itself is hidden, and you can restore it later from "Show archived
                herds" below.
              </span>
              {archiveError && (
                <p className="livestock-page__field-error" role="alert">
                  {archiveError}
                </p>
              )}
              <button
                type="button"
                className="btn btn--danger livestock-page__btn"
                disabled={archiving}
                onClick={() => void confirmArchive()}
              >
                {archiving ? "…" : "Archive"}
              </button>
              <button
                type="button"
                className="btn btn--ghost livestock-page__btn"
                disabled={archiving}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </button>
            </span>
          )}
        </RowActions>
      )}
    </h3>
  );
}

